import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Redirect, useHistory } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

import styles from '../../views/ProductDetails/ProductDetails.module.scss';
import miniPDPStyles from './MiniPDP.module.scss';
import HideChatWidget from '../../components/ZendeskChat/HideChatWidget';
import AppPaths from '../../AppPaths';
import { getFiGrowthBook } from '../../lib/growthbook';
import { useFeatureValue } from '@growthbook/growthbook-react';
import CollarAddToBagButton from '../../views/ProductDetails/Series3CollarDetails/components/CollarAddToBagButton';
import { DesktopImageGallery } from './MiniImageGallery/MiniImageGallery';
import * as types from '../../types';
import { Species } from '../../types/gql-op-types';
import Footer from '../Footer/Footer';
import { Carousel, CarouselController } from '../Carousel';
import { useSpeciesUrlParam } from '../../hooks/useSpeciesUrlParam';
import SubscriptionOptions from '../SubscriptionOptions';
import MiniAllFeatures from './MiniAllFeatures/MiniAllFeatures';
import MiniProductSummary from './MiniProductSummary/MiniProductSummary';
import PlusMembershipValueProps from '../../views/ProductDetails/Series3CollarDetails/components/PlusMembershipValueProps';
import { cartActions } from '../../reducers/storeCart';
import * as events from '../../lib/analytics/events';
import {
  getAnalyticsProductPriceInCents,
  getAnalyticsSubscriptionValueInCents,
} from '../../lib/analytics/analyticsUtils';
import { Image as FiImage } from '../../components/Image';
import MiniFAQ from './MiniFAQ/MiniFAQ';
import PlusNewFeatures from '../../views/ProductDetails/Series3CollarDetails/components/PlusNewFeatures';
import useSubscriptionProducts from '../../hooks/useSubscriptionProducts';
import { collarProductIsSupportedInCountry, useLocale } from '../../contexts/LocaleContext';
import NavBar from '../AppBar/NavBar';
import SubscriptionUpsellModal from '../SubscriptionUpsellModal';
import { getNanoVariantPetName, showNanoVariant } from '../NanoBanner/NanoBanner';
import NanoProductDetails from '../../views/ProductDetails/Series3CollarDetails/components/NanoProductDetails/NanoProductDetails';
import { showRescueGwaVariant } from '../../views/RescueLanding/RescueBanner';
import { CustomerIO } from '../../lib/analytics/customerIO';
import { isInAppShop } from '../../lib/inAppShop';
import { Box, Flex, Text } from '@mantine/core';
import { useIsGift } from '../../lib/util/isGift';
import { ReactComponent as IconInfo } from '../../assets/images/icons/icon_info_transparent.svg';
import { SaleBadge } from '../SaleBadge/SaleBadge';
import { getFreeTrialDurationLabel } from '../../lib/subscription';
import { usePdpImagesContext } from '../../contexts/PdpImagesContext';
import useFreeTrialSubscriptionPrice from '../../hooks/useFreeTrialSubscriptionPrice';
import useIsReferralCheckoutV2 from '../../hooks/useIsReferralCheckoutV2';
import StarRating from '../StarRating/StarRating';
import OutcomesChecklist from '../OutcomesChecklist/OutcomesChecklist';
import useFreeTrialPromoVisible from '../../hooks/useFreeTrialPromoVisible';
import { centsToDollarsNumber, getLocalizedPricing } from '../../lib/util';
import useFreeTrialEligibility from '../../hooks/useFreeTrialEligibility';
import { captureQuizReferralFromUrl, getQuizReferral, QUIZ_PRODUCT_MINI } from '../../lib/quizReferral';

function MiniAppCarousel({ species }: { species: Species }) {
  const ecomMiniBehavior = getFiGrowthBook().getFeatureValue<boolean>('ecom-mini-behavior', false);

  const catImages = ['/product_images/smart-collar-v3b/app-ui-homescreen-1-cat.png'];

  const dogImages = [
    '/product_images/smart-collar-v3b/app-ui-homescreen-1-dog.png',
    ...(ecomMiniBehavior
      ? [
          '/product_images/smart-collar-v3b/app-ui-homescreen-2-dog.png',
          '/product_images/smart-collar-v3b/app-ui-homescreen-3-dog.png',
        ]
      : []),
  ];

  const images = species === Species.CAT ? catImages : dogImages;

  return (
    <Carousel
      className={miniPDPStyles.mobileAppCarousel}
      wrapAround={true}
      prevNextButtons={species === Species.DOG && ecomMiniBehavior}
    >
      {images.map((url, index) => (
        <div key={index} className={miniPDPStyles.mobileAppImage}>
          <FiImage
            image={{
              type: 'image',
              url: url,
              descriptors: ['1000w'],
            }}
            loading="eager"
          />
        </div>
      ))}
    </Carousel>
  );
}

const useUpsellSubscription = (
  selectedSubscription: types.ISubscriptionProduct | undefined,
  subscriptionProducts: types.ISubscriptionProduct[],
) => {
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const upsellEnabled = getFiGrowthBook().getFeatureValue<boolean>('enable-3mo-mini-upsell', false);

  const subscriptionToUpsell: types.ISubscriptionProduct | undefined = useMemo(() => {
    if (!selectedSubscription) {
      return undefined;
    }

    const planCodeToUpsell = selectedSubscription.attemptUpsellToPlanCode;
    if (!planCodeToUpsell) {
      return undefined;
    }

    return subscriptionProducts.find((p) => p.sku === planCodeToUpsell);
  }, [selectedSubscription, subscriptionProducts]);

  return { showUpsellModal, setShowUpsellModal, subscriptionToUpsell, upsellEnabled };
};

export interface MiniPDPProps {
  availableSubscriptions?: types.ISubscriptionProduct[];
  hasValidRedemptionCode?: boolean;
  redemptionType?: types.RedemptionType | null;
  preselectedSubscriptionSku?: string;
}

function ProductTitle({ species, freeTrialEnabled }: { species: Species; freeTrialEnabled: boolean }) {
  const isGift = useIsGift();
  const { t } = useTranslation();
  const saleBadge = useFeatureValue('ecom-bfcm-badge', 0);

  if (isGift) {
    return (
      <Flex align={'baseline'} gap={8}>
        <Text size={48} fw={700} lh={1.05} sx={{ letterSpacing: '-0.96px' }}>
          <Trans i18nKey="give_the_gift_of_fi_mini" components={{ br: <br /> }} />
        </Text>
      </Flex>
    );
  }

  return (
    <div className={miniPDPStyles.productTitle}>
      {t('mini')}{' '}
      <span className={miniPDPStyles.productTitleSubtitle}>{species === Species.CAT ? t('for_cats') : ''}</span>
      <SaleBadge className={miniPDPStyles.saleBadge} freeTrialEnabled={freeTrialEnabled} />
      {!saleBadge && !freeTrialEnabled && <span className={miniPDPStyles.newLabel}>{t('new')}</span>}
    </div>
  );
}

function ProductTitleDescription({
  species,
  showNanoDescription,
  quizPetName,
}: {
  species: Species;
  showNanoDescription: boolean;
  quizPetName?: string;
}) {
  const isGift = useIsGift();
  const { t } = useTranslation();
  if (isGift) {
    return <div className={miniPDPStyles.productTitleDescription}>{t('gift_a_fi_mini')}</div>;
  }

  if (showNanoDescription) {
    const petName = getNanoVariantPetName(species);
    return (
      <div className={miniPDPStyles.productTitleDescription}>
        {t('microchips_are_great_start_but_they_cant_tell_you_where_pet_is', { petName })}
      </div>
    );
  }

  if (quizPetName) {
    return (
      <div className={miniPDPStyles.productTitleDescription}>
        {t('quiz_mini_subtitle_for_pet', { petName: quizPetName })}
      </div>
    );
  }

  return (
    <div className={miniPDPStyles.productTitleDescription}>
      {species === Species.DOG
        ? t('track_your_dog_with_our_smallest_most_flexible_gps_device_yet')
        : t('slim_light_and_made_to_fit_any_setup_our_gps_tracker_built_for_curious_cats')}
    </div>
  );
}

function ProductGiftDisclaimer() {
  const { t } = useTranslation();
  return (
    <Flex align="center" gap={8} mb={24} sx={(theme) => ({ color: theme.colors['type-light-secondary'] })}>
      <Box h={40} w={40} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <IconInfo />
      </Box>
      <Text size={12} fw={500} lh={1.4}>
        {t('gift_disclaimer')}
      </Text>
    </Flex>
  );
}

export default function MiniPDP({
  availableSubscriptions,
  hasValidRedemptionCode,
  redemptionType,
  preselectedSubscriptionSku,
}: MiniPDPProps) {
  const isGift = useIsGift();
  const isReferralV2 = useIsReferralCheckoutV2();

  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const products = useSelector((state: types.AppState) => state.config.products);
  const siteConfig = useSelector((state: types.AppState) => state.config.siteConfig);
  const [species, setSpecies] = useSpeciesUrlParam();
  const carouselControllerRef = useRef<CarouselController>();
  const previousCarouselIndexRef = useRef(0);

  // Get pre-loaded dynamic images from context
  const { getMedia } = usePdpImagesContext();
  const dynamicImages = isGift ? null : getMedia(types.miniCollarId, { species: species.toLowerCase() });
  const [currentCarouselImage, setCurrentCarouselImage] = useState<number>(0);
  const specsRef = useRef<HTMLDivElement | null>(null);
  const featuresRef = useRef<HTMLDivElement | null>(null);
  const miniPlusRef = useRef<HTMLDivElement | null>(null);
  const faqRef = useRef<HTMLDivElement | null>(null);
  const { country, currency, currencyFormatter } = useLocale();
  const referrerName = useSelector((state: types.AppState) => state.storeShop.cart.referrerName);
  const rescueGwaVariantEnabled = showRescueGwaVariant();
  const nanoVariantEnabled = showNanoVariant();
  const simplifiedVariantEnabled = rescueGwaVariantEnabled || nanoVariantEnabled;
  const ecomMiniBehavior = getFiGrowthBook().getFeatureValue<boolean>('ecom-mini-behavior', false);

  const product = products.find((p) => p.category === types.ProductCategory.MINI) as types.IMiniProduct | undefined;
  const allMiniSubscriptions = useSubscriptionProducts(product?.id ?? types.miniCollarId);
  const isCat = species === Species.CAT;
  const filteredSubscriptions = allMiniSubscriptions.filter(
    (p) =>
      (siteConfig.miniPlusByDefault ? p.isPlusPlan : !p.isPlusPlan) &&
      // filter out the Verizon plan (or any third-party plans) on D2C PDPs
      !p.thirdParty,
  );
  // cats don't support free trial — strip the flag so plans show as regular options,
  // but drop free trial plans that already have a non-free-trial equivalent
  const nonFreeTrialMonths = new Set(filteredSubscriptions.filter((p) => !p.freeTrial).map((p) => p.renewalMonths));
  const defaultSubscriptions = isCat
    ? filteredSubscriptions
        .filter((p) => !p.freeTrial || !nonFreeTrialMonths.has(p.renewalMonths))
        .map((p) => (p.freeTrial ? { ...p, freeTrial: false } : p))
    : filteredSubscriptions;
  const sixMonthSubscriptions = defaultSubscriptions.filter((s) => s.renewalMonths === 6);
  const giftSubscriptions = defaultSubscriptions.filter((s) => [6, 12, 24].includes(s.renewalMonths));
  const subscriptions = isGift
    ? giftSubscriptions
    : simplifiedVariantEnabled
      ? sixMonthSubscriptions
      : (availableSubscriptions ?? defaultSubscriptions);
  let initialSubscription = preselectedSubscriptionSku
    ? subscriptions.find((s) => s.sku === preselectedSubscriptionSku)
    : undefined;

  if (initialSubscription === undefined && simplifiedVariantEnabled) {
    initialSubscription = subscriptions.at(0);
  }

  const { isEligible: isFreeTrialEligible } = useFreeTrialEligibility(product?.id ?? types.miniCollarId);
  const referralFreeTrialSubscription =
    isReferralV2 && isFreeTrialEligible ? subscriptions.find((s) => s.freeTrial) : undefined;

  const quizReferral = getQuizReferral();
  const quizReferredMini = !isGift && quizReferral?.recommendedProduct === QUIZ_PRODUCT_MINI;
  const quizPetName = quizReferredMini ? quizReferral?.petName : undefined;
  // Quiz trial CTA continuity: preselect the 12-month free-trial plan the quiz offered.
  const freeTrialPlans = subscriptions.filter((s) => s.freeTrial);
  const quizTrialFreeTrialSubscription =
    quizReferredMini && quizReferral?.ctaType === 'trial' && isFreeTrialEligible
      ? (freeTrialPlans.find((s) => s.renewalMonths === 12) ?? freeTrialPlans[0])
      : undefined;

  const [selectedSubscription, setSelectedSubscription] = useState<types.ISubscriptionProduct | undefined>(
    referralFreeTrialSubscription ?? quizTrialFreeTrialSubscription ?? initialSubscription,
  );
  const subscriptionPickerRef = useRef<HTMLDivElement>(null);

  const { showUpsellModal, setShowUpsellModal, subscriptionToUpsell, upsellEnabled } = useUpsellSubscription(
    selectedSubscription,
    subscriptions,
  );

  const freeTrialPromoVisible = useFreeTrialPromoVisible();
  const freeTrialEnabled = freeTrialPromoVisible && subscriptions.some((p) => p.freeTrial);
  const freeTrialSubscriptionPrice = useFreeTrialSubscriptionPrice(product?.id ?? types.miniCollarId);

  useEffect(() => {
    captureQuizReferralFromUrl();
  }, []);

  // Analytics - Mini product page viewed
  useEffect(() => {
    if (!product) return;
    const totalPriceInCents =
      getAnalyticsProductPriceInCents(product, currency) +
      (selectedSubscription ? getAnalyticsProductPriceInCents(selectedSubscription, currency) : 0);

    events.miniProductViewed({
      priceInCents: totalPriceInCents,
      productName: product.name,
      sku: product.sku,
      isGift,
      currency,
      analyticsSubscriptionValueInCents: selectedSubscription
        ? getAnalyticsSubscriptionValueInCents(selectedSubscription, currency)
        : undefined,
    });
  }, [product, selectedSubscription, isGift, currency]);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    const observeOnce = (element: Element | null, onIntersect: () => void) => {
      if (!element) return;
      const observer = new IntersectionObserver(
        (entries, obs) => {
          const isVisible = entries.some((e) => e.isIntersecting);
          if (isVisible) {
            onIntersect();
            obs.disconnect();
          }
        },
        { threshold: 0.25 },
      );
      observer.observe(element);
      observers.push(observer);
    };

    observeOnce(specsRef.current, () => events.miniViewedSpecifications());
    observeOnce(featuresRef.current, () => events.miniViewedFeatures());
    observeOnce(miniPlusRef.current, () => events.miniViewedMiniPlus());
    observeOnce(faqRef.current, () => events.miniViewedFAQ());

    return () => {
      observers.forEach((o) => o.disconnect());
    };
  }, []);

  const addToBag = useCallback(
    (subscription: types.ISubscriptionProduct | undefined) => {
      if (!subscription || !product) {
        subscriptionPickerRef.current?.scrollIntoView({ behavior: 'smooth' });
        events.miniPickYourMembershipClicked();
        return false;
      }

      // Fire add to bag analytics event
      const totalPriceInCents =
        getAnalyticsProductPriceInCents(product, currency) + getAnalyticsProductPriceInCents(subscription, currency);

      events.miniAddedToBag({
        priceInCents: totalPriceInCents,
        productName: product.name,
        sku: product.sku,
        isGift,
        currency,
      });

      dispatch(
        cartActions.addBundleCartItem({
          sku: product.sku,
          subscriptionSku: subscription.sku,
          addonSkus: [],
          currency,
          isGift: isGift,
          species,
        }),
      );

      CustomerIO.trackAddedItemToCart(types.miniCollarId);
      if (isInAppShop()) {
        dispatch(cartActions.setShowInAppCheckout(true));
      } else {
        history.push(AppPaths.Bag);
      }
      return true;
    },
    [dispatch, product, history, subscriptionPickerRef, currency, isGift, species],
  );

  const onAddToBagClicked = useCallback(() => {
    if (subscriptionToUpsell && upsellEnabled) {
      setShowUpsellModal(true);
      return false;
    }

    return addToBag(selectedSubscription);
  }, [setShowUpsellModal, subscriptionToUpsell, addToBag, selectedSubscription, upsellEnabled]);

  // Handler for when user clicks "Start free trial" in the modal - adds to cart
  const handleFreeTrialAddToCart = useCallback(
    (subscriptionSku: string) => {
      const subscription = subscriptions.find((s) => s.sku === subscriptionSku);
      if (subscription) {
        addToBag(subscription);
      }
    },
    [subscriptions, addToBag],
  );

  const config = useSelector((state: types.AppState) => state.config.siteConfig);
  const miniPDPEnabled = getFiGrowthBook().getFeatureValue<boolean>('ecom-mini-pdp', false, {
    internalTester: config.internalTester,
  });
  if (!miniPDPEnabled) {
    return <Redirect to={AppPaths.NotFound} />;
  }
  if (!collarProductIsSupportedInCountry(types.miniCollarId, country)) {
    return <Redirect to={AppPaths.Products(types.series3PlusCollarId)} />;
  }

  let lightModeImages: number[] = [];
  if (species === Species.CAT) {
    lightModeImages = [0, 2, 3];
  } else {
    lightModeImages = [0, 2, 3, 4];
  }
  const isLightMode = lightModeImages.includes(currentCarouselImage);

  return (
    <>
      {selectedSubscription && subscriptionToUpsell && upsellEnabled && (
        <SubscriptionUpsellModal
          onAccept={() => addToBag(subscriptionToUpsell)}
          onClose={() => setShowUpsellModal(false)}
          onIgnore={() => addToBag(selectedSubscription)}
          open={showUpsellModal}
          selectedSubscription={selectedSubscription}
          subscriptionToUpsell={subscriptionToUpsell}
        />
      )}
      <div
        className={classNames(styles.productContainer, styles.ecomFlowRevamp, 'ecom-flow-revamp', 'mini-pdp', {
          'ecom-flow-revamp-light-v2': isLightMode,
        })}
      >
        <NavBar />
        <div className={classNames('image-gallery-container', styles.galleryContainer)}>
          <DesktopImageGallery
            carouselControllerRef={carouselControllerRef}
            species={species}
            onChange={(idx) => {
              if (idx !== previousCarouselIndexRef.current) {
                const direction = idx > previousCarouselIndexRef.current ? 'next' : 'prev';
                events.miniCarouselScrolled(idx, direction);
                previousCarouselIndexRef.current = idx;
              }
              setCurrentCarouselImage(idx);
            }}
            dynamicImages={dynamicImages}
          />
        </div>
        <div className={classNames(styles.productDetails, 'product-details')}>
          <div className={styles.productTitleContainer}>
            {isReferralV2 && <StarRating />}
            <ProductTitle species={species} freeTrialEnabled={freeTrialEnabled} />
            <ProductTitleDescription
              species={species}
              showNanoDescription={nanoVariantEnabled && !rescueGwaVariantEnabled}
              quizPetName={quizPetName}
            />
            {isReferralV2 && <OutcomesChecklist />}

            <div className={miniPDPStyles.speciesSwitcher}>
              <div className={miniPDPStyles.switcherContainer}>
                <div
                  className={classNames(miniPDPStyles.switcherSlider, {
                    [miniPDPStyles.sliderCat]: species === Species.CAT,
                  })}
                />
                <button
                  className={classNames(miniPDPStyles.switcherOption, {
                    [miniPDPStyles.active]: species === Species.DOG,
                  })}
                  onClick={() => {
                    setSpecies(Species.DOG);
                    events.miniSpeciesToggle(Species.DOG);
                    carouselControllerRef.current?.select(0);
                  }}
                  type="button"
                >
                  {t('dog')}
                </button>
                <button
                  className={classNames(miniPDPStyles.switcherOption, {
                    [miniPDPStyles.active]: species === Species.CAT,
                  })}
                  onClick={() => {
                    setSpecies(Species.CAT);
                    events.miniSpeciesToggle(Species.CAT);
                    carouselControllerRef.current?.select(0);
                  }}
                  type="button"
                >
                  {t('cat')}
                </button>
              </div>
            </div>

            <SubscriptionOptions
              allowNoSubscription={false}
              onSubscriptionChange={(subscription) =>
                setSelectedSubscription(subscriptions.find((s) => s.sku === subscription))
              }
              selectedSubscriptionSku={selectedSubscription?.sku}
              subscriptionProducts={subscriptions}
              title={isGift ? 'Pick a gift value' : simplifiedVariantEnabled ? '' : t('membership')}
              subtitle={simplifiedVariantEnabled ? undefined : t('every_new_membership_includes_a_free_fi_mini')}
              showReferralPill={false}
              couponPillDisplay={undefined}
              productName={''}
              productCategory={types.ProductCategory.MINI}
              subscriptionPickerRef={subscriptionPickerRef}
              startAsUnselected={
                !preselectedSubscriptionSku && !referralFreeTrialSubscription && !quizTrialFreeTrialSubscription
              }
              disableSection={false}
              prominentPricingDesign={false}
              hasValidRedemptionCode={!!hasValidRedemptionCode}
              redemptionType={redemptionType}
              onFreeTrialAddToCart={handleFreeTrialAddToCart}
              collarProductId={types.miniCollarId}
            />
            {isGift && <ProductGiftDisclaimer />}
            <PlusMembershipValueProps freeTrialEnabled={freeTrialEnabled} />
            <CollarAddToBagButton
              ctaText={
                !selectedSubscription
                  ? t('pick_your_membership')
                  : isReferralV2
                    ? referrerName
                      ? t('referral_v2_join_on_fi', { referrerName })
                      : t('referral_v2_join_fi')
                    : t('add_to_cart')
              }
              descriptionText={
                selectedSubscription
                  ? selectedSubscription.freeTrial && selectedSubscription.freeTrialDays
                    ? t('free_trial_duration', {
                        duration: getFreeTrialDurationLabel(selectedSubscription.freeTrialDays),
                      })
                    : selectedSubscription.renewalMonths === 12
                      ? t('1_year')
                      : selectedSubscription.renewalMonths === 24
                        ? '2 Years'
                        : selectedSubscription.renewalMonths === 6
                          ? '6 Months'
                          : '1 month'
                  : null
              }
              fadeInRef={selectedSubscription ? undefined : subscriptionPickerRef}
              isVisibleOverride={!!selectedSubscription}
              onAddToBag={() => {
                return onAddToBagClicked();
              }}
              subtitleText={
                isReferralV2 && selectedSubscription
                  ? selectedSubscription.freeTrial && freeTrialSubscriptionPrice
                    ? t('referral_v2_renews_yearly_after_trial', { price: freeTrialSubscriptionPrice })
                    : t('referral_v2_renews_at_price_per_period', {
                        price: currencyFormatter.format(
                          centsToDollarsNumber(getLocalizedPricing(selectedSubscription.pricingInCents, currency)),
                        ),
                        period: t(`referral_v2_period_${selectedSubscription.renewalMonths}`),
                      })
                  : selectedSubscription?.freeTrial && freeTrialSubscriptionPrice
                    ? t('membership_after_trial', { price: freeTrialSubscriptionPrice })
                    : null
              }
            />
          </div>
        </div>
      </div>
      {species === Species.DOG && ecomMiniBehavior && <PlusNewFeatures containerRef={miniPlusRef} />}
      {nanoVariantEnabled && !rescueGwaVariantEnabled && <NanoProductDetails productType="mini" />}
      <MiniProductSummary containerRef={specsRef} species={species} />
      <MiniAppCarousel species={species} />
      <MiniAllFeatures containerRef={featuresRef} species={species} />
      <MiniFAQ containerRef={faqRef} species={species} />
      <div className={miniPDPStyles.footerContainer}>
        <Footer />
      </div>
      <HideChatWidget />
    </>
  );
}
